import React, { useState, useEffect } from "react";
import Footer from "../component/Footer/FooterCommon";
import CompanySelect from "../component/Common/CompanySelect";
import Navbar from "../component/Navbar/NavbarCommon";
import { useNavigate } from 'react-router-dom';
import { api } from "../api";
import { API_URL, ASSETS_URL } from "../utils/config";
import { toast } from "react-toastify";
import { useAccountStore } from "../store/useAccountStore";
import ArrowButton from "../component/Common/ArrowButton";

interface DownloadItem {
  _id: string;
  title: string;
  document: string;
  createdAt?: string;
  updatedAt?: string;
}

const Downloads = () => {
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [downloadList, setDownloadList] = useState<DownloadItem[]>([]);
  const { user } = useAccountStore();
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllDownloads();
  }, []);

  const fetchAllDownloads = async () => {
    try {
      const { data, error } = await api.get(`${API_URL.downloads}?country=${user?.company}`);
      
      if (error) {
        toast.error(error.message);
        return;
      }
      
      if (data) {
        setDownloadList((data as any)?.data?.downloads || []);
      }
    } catch (error) {
      console.error("Error fetching downloads:", error);
      toast.error("Failed to load downloads");
    }
  };

  const handleDownloadClick = (documentUrl: string, title: string) => {
    window.open(  `${ASSETS_URL}${documentUrl}`, '_blank', 'noopener,noreferrer');
  };

  // Improved scroll handler with better sensitivity
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDifference = currentScrollY - lastScrollY;

      // Minimum threshold to prevent micro-scrolls from triggering
      if (Math.abs(scrollDifference) < 10) return;

      // Show navbar when:
      // 1. Scrolling up significantly (at least 50px)
      // 2. Near the top of the page (within 50px)
      if (scrollDifference < -50 || currentScrollY < 50) {
        setIsNavbarVisible(true);
      }
      // Hide navbar when:
      // 1. Scrolling down significantly (at least 80px)
      // 2. Not near the top of the page (more than 100px from top)
      else if (scrollDifference > 80 && currentScrollY > 100) {
        setIsNavbarVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Navbar with scroll behavior */}
      <Navbar isVisible={isNavbarVisible} />

      {/* Fixed Header - positioned below navbar with reduced height */}
      <div
        className="fixed left-0 right-0 bg-white z-40 md:mt-6"
        style={{
          top: isNavbarVisible ? '100px' : '0px',
          transition: 'top 0.3s ease-in-out'
        }}
      >
        <div className="px-3 sm:px-4 lg:px-10 py-2">
          {/* Header - reduced gap and sizing */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-0">
            <CompanySelect />
          </div>

          {/* Action buttons section */}
          <div className="mt-2 sm:mt-3">
            <div className="mb-2">
              <ArrowButton />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area - This will be clipped to prevent cards from showing in header area */}
      <div
        className="flex-1 relative"
        style={{
          marginTop: isNavbarVisible ? '180px' : '100px',
          transition: 'margin-top 0.3s ease-in-out'
        }}
      >
        {/* Scrollable Download Cards Section with proper clipping */}
        <div className="h-full overflow-y-auto pb-16 relative">
          {/* Invisible overlay to clip cards in header area */}
          <div
            className="fixed left-0 right-0 bg-white z-30 pointer-events-none"
            style={{
              top: 0,
              height: isNavbarVisible ? '180px' : '100px',
              transition: 'height 0.3s ease-in-out'
            }}
          />

          <div className="px-3 sm:px-4 lg:px-10 pt-20 lg:pt-16 relative z-20">
            {/* Title with subtitle */}
            <div className="mb-6 sm:mb-8 md:mb-10 text-center sm:text-left mt-2">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-black mb-2 sm:mb-3 leading-tight">
                <span className="text-navgreen">Downloads</span> & Documents
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 font-medium leading-relaxed max-w-4xl">
                Access important documents and resources for your reference.
              </p>
            </div>

            {/* Downloads Section */}
            <div className="flex flex-col md:grid-cols-3 md:grid gap-5 sm:grid-cols-2 sm:grid">
              {downloadList && downloadList.length > 0 ? (
                downloadList.map((item, index) => (
                  <div
                    key={item._id || index}
                    onClick={() => handleDownloadClick(item.document, item.title)}
                    className="px-3 py-4 flex border-navgreen border-r-2 transition-all duration-300 transform hover:-translate-y-1 border-b-2 overflow-hidden shadow-[0_0_2px_rgba(0,0,0,0.15)] hover:shadow-md rounded-md gap-2 items-center cursor-pointer bg-white hover:bg-green-50"
                  >
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold font-poppins text-sm text-gray-900">
                        {item.title}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-600 text-lg">No document found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white z-50 shadow-lg">
        <div className="px-3 sm:px-4 lg:px-10 py-4">
          {/* Footer content */}
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default Downloads;
